import React, { useEffect, useRef, useState } from 'react';
import mapBg from '../assets/testimonial-section/map.png';
import mobileLogo from '../assets/testimonial-section/black-mobile.png';
import reviewBox from '../assets/testimonial-section/review-box.png';
import { fetchTestimonials } from '../services/api';

// Add TypewriterText component
const TypewriterText = ({ text, isActive, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!isActive) {
      if (!hasCompleted.current) {
        setDisplayedText('');
        setCurrentIndex(0);
      }
      return;
    }

    if (!hasCompleted.current && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, currentIndex === 0 ? delay : 90);

      return () => clearTimeout(timeout);
    }
  }, [isActive, currentIndex, text, delay]);

  useEffect(() => {
    if (currentIndex === text.length) {
      hasCompleted.current = true;
    }
  }, [currentIndex, text]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const ReviewBox = ({ testimonial, className = "", style = {} }) => {
  return (
    <div className={`relative ${className}`} style={style}>
      <img
        src={reviewBox}
        alt="Review box"
        className="absolute w-full h-full"
      />
      <div className="absolute inset-0 flex flex-col justify-start gap-2 md:gap-2 items-start py-4 pl-12 pr-4 text-black">
        <p className="text-xs md:text-sm font-bold mt-2">{testimonial.author}, 
          <span className="text-[10px] md:text-[10px] text-[#4c4c4d]"> {testimonial.designation}</span>
        </p>
        <p className="text-xs md:text-[10px] text-left italic ">
          "{testimonial.message}"
        </p>
      </div>
    </div>
  );
};

const MapTestimonial = () => {
  const [inView, setInView] = useState(false);
  const [visibleReviews, setVisibleReviews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const sectionRef = useRef(null);

  // Define positions for desktop review boxes
  const desktopPositions = [
    { top: '10%', left: '17%', delay: 600 },
    // { top: '0%', left: '30%', delay: 600 },
    { top: '20%', left: '43%', delay: 1400 },
    { top: '0%', left: '25%', delay: 2200 },
    { top: '-70%', left: '53%', delay: 2600 },
    // { top: '20%', left: '43%', delay: 1400 },
    // { top: '0%', left: '25%', delay: 2200 },
    // { top: '-70%', left: '53%', delay: 2600 }
  ];

  useEffect(() => {
    // Fetch testimonials
    const loadTestimonials = async () => {
      const data = await fetchTestimonials();
      setTestimonials(data);
    };

    loadTestimonials();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);

          let delay = 0;
          const delays = [];
          desktopPositions.forEach((_, i) => {
            delays.push(setTimeout(() => {
              setVisibleReviews(prev => [...prev, i]);
            }, delay));
            delay += 200;
          });

          return () => delays.forEach(t => clearTimeout(t));
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="w-full text-white relative py-8 md:py-12 overflow-hidden">
      <style>
        {`
          @keyframes hoverAnimation {
            0% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0); }
          }
          
          @keyframes slideInRight {
            from { transform: translateX(150px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          
          @keyframes popIn {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          
          @keyframes fadeInUp {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          .animate-hover {
            animation: hoverAnimation 2s ease-in-out infinite;
          }
          
          .animate-slide-right {
            animation: slideInRight 0.8s ease-out forwards;
          }
          
          .animate-pop-in {
            animation: popIn 1.8s ease-in forwards;
          }
          
          .animate-fade-up {
            animation: fadeInUp 0.8s ease-out forwards;
          }
          
          /* Marquee Styles */
          .marquee-container {
            overflow: hidden;
            position: relative;
            width: 100%;
          }
          
          .marquee-content {
            display: flex;
            width: 200%;
            animation: marquee 20s linear infinite;
          }
          
          .marquee-item {
            flex: 0 0 auto;
          }
          
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}
      </style>

      <div className="text-center px-4">
        <h2 className={`text-4xl md:text-4xl lg:text-6xl font-bold transition-opacity duration-700 ${inView ? 'opacity-100 animate-fade-up' : 'opacity-0'
          }`}>
          {inView && (
            <TypewriterText
              text="Words from our Clients"
              isActive={inView}
              delay={0}
            />
          )}
        </h2>
        <p className={`text-xl md:text-lg lg:text-4xl py-3 md:py-5 text-white font-bold transition-opacity duration-700 ${inView ? 'opacity-100 animate-fade-up' : 'opacity-0'
          }`} style={{ animationDelay: '2000ms' }}>
          The Big Blue Way
        </p>
      </div>

      {/* Desktop View */}
      {/* Desktop View */}
      <div className="hidden md:block w-full h-[500px] md:h-[600px] relative mt-4 md:mt-8">
        <img
          src={mapBg}
          alt="World Map"
          className="w-full h-full object-contain absolute top-0 left-0"
        />

        {/* Desktop Review Positions with testimonials */}
        {testimonials.slice(0, desktopPositions.length).map((testimonial, index) => (
          <ReviewBox
            key={testimonial.id}
            testimonial={testimonial}
            className={`w-32 md:w-72 h-32 md:h-40 z-50 ${visibleReviews.includes(index) ? 'animate-hover' : 'opacity-0'
              }`}
            style={{
              top: desktopPositions[index].top,
              left: desktopPositions[index].left,
              animationDelay: `${desktopPositions[index].delay}ms`
            }}
          />
        ))}

        <img
          src={mobileLogo}
          alt="Big Blue Logo"
          className={`absolute w-1/3 h-auto right-0 bottom-0 ${inView ? 'animate-slide-right' : 'opacity-0'
            }`}
          style={{ animationDelay: '1000ms' }}
        />
      </div>

      {/* Mobile View */}
      <div className="md:hidden relative h-[400px] w-full overflow-hidden mb-7">
        <img
          src={mapBg}
          alt="World Map Background"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />

        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`marquee-container w-full h-44 py-10 flex items-center ${inView ? 'opacity-100' : 'opacity-0'
            }`}>
            <div className="marquee-content">
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div key={`${testimonial.id}-${index}`} className="marquee-item mx-4">
                  <ReviewBox
                    testimonial={testimonial}
                    className={`w-80 h-[180px] ${inView ? 'animate-pop-in' : 'opacity-0'
                      }`}
                    style={{ animationDelay: `${index * 200}ms` }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapTestimonial;